import {Component, computed, DestroyRef, effect, ElementRef, inject, input, OnDestroy, OnInit, signal, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {ContentTypeModel, DownloadResolution} from "@generated"
import {ImageViewerComponent} from "@common/components/viewers"
import {DropFilesComponent} from "@common/components/files"
import {ColormassEntity} from "@cm/auth"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {RefreshService} from "@common/services/refresh/refresh.service"
import {UploadGqlService} from "@common/services/upload/upload.gql.service"
import {ThumbnailsService} from "@platform/services/thumbnails/thumbnails.service"
import {GalleryImageService} from "@common/services/gallery-image/gallery-image.service"
import {IsTruthy} from "@cm/utils/filter"
import {EndpointUrls} from "@common/models/constants/urls"
import {MimeType} from "@legacy/helpers/utils"
import {BehaviorSubject, filter, map, switchMap} from "rxjs"
import {MemoizePipe} from "@app/common/pipes/memoize/memoize.pipe"
import {DataObjectThumbnailComponent} from "@common/components/data-object-thumbnail/data-object-thumbnail.component"
import {ThumbnailLayout} from "@common/models/enums/thumbnail-layout"
import {FourSquareLayoutComponent} from "@common/components/layouts/four-square-layout/four-square-layout.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-gallery-image",
    templateUrl: "./gallery-image.component.html",
    styleUrl: "./gallery-image.component.scss",
    imports: [DataObjectThumbnailComponent, ImageViewerComponent, DropFilesComponent, MemoizePipe, FourSquareLayoutComponent],
})
export class GalleryImageComponent<
        ItemType extends {
            id: string
            galleryImage?: {id: string} | null
            organization?: {
                id: string
            } | null
        } & ColormassEntity,
    >
    implements OnInit, OnDestroy
{
    item$ = new BehaviorSubject<ItemType | null | undefined>(undefined)
    readonly $item = input.required<ItemType | null | undefined>({alias: "item"})

    readonly $multiThumbnailIds = input<string[] | null>(null, {alias: "multiThumbnailIds"})

    constructor() {
        effect(() => {
            const item = this.$item()
            this.item$.next(item)
        })
    }
    readonly $imageViewer = viewChild.required<ImageViewerComponent>("imageViewer")

    readonly destroyRef = inject(DestroyRef)
    readonly elementRef = inject(ElementRef)
    readonly galleryImageService = inject(GalleryImageService)
    readonly notifications = inject(NotificationsService)
    readonly permission = inject(PermissionsService)
    readonly refresh = inject(RefreshService)
    readonly thumbnails = inject(ThumbnailsService)
    readonly uploadService = inject(UploadGqlService)
    $can = this.permission.$to

    uploadProcessingUrl = EndpointUrls.DATA_OBJECT_PROCESSING_URL

    readonly $galleryImage = signal<{id: string} | undefined>(undefined)
    readonly $mouseHovering = signal<boolean>(false)
    readonly $showDropzone = computed(
        () => !this.$galleryImage() && this.$mouseHovering() && (this.item$.value ? this.$can().update.item(this.item$.value, "galleryImage") : false),
    )

    ngOnInit() {
        this.item$
            .pipe(
                switchMap((item) => this.refresh.observeItem$(item).pipe(map(() => item))),
                takeUntilDestroyed(this.destroyRef),
                filter(IsTruthy),
                map((item) => this.$galleryImage.set(item.galleryImage ?? undefined)),
            )
            .subscribe()

        this.elementRef.nativeElement.addEventListener("mouseenter", this.onMouseEnter.bind(this))
        this.elementRef.nativeElement.addEventListener("mouseleave", this.onMouseLeave.bind(this))
    }

    ngOnDestroy() {
        this.elementRef.nativeElement.removeEventListener("mouseenter", this.onMouseEnter.bind(this))
        this.elementRef.nativeElement.removeEventListener("mouseleave", this.onMouseLeave.bind(this))
    }

    onMouseEnter() {
        this.$mouseHovering.set(true)
    }

    onMouseLeave() {
        this.$mouseHovering.set(false)
    }

    openImageViewer(): void {
        if (this.$showDropzone()) return
        const galleryImage = this.$galleryImage()
        if (galleryImage) {
            this.$imageViewer().openViewer(galleryImage.id, [galleryImage.id])
        } else {
            this.notifications.showInfo("No images to display")
        }
    }

    async assignGalleryImage(files: File[]) {
        if (files.length !== 1) {
            this.notifications.showError("Please drop a single image file")
            return
        }
        const item = this.item$.value
        let organizationId: string | undefined
        switch (item?.__typename) {
            case ContentTypeModel.Organization:
                organizationId = item.id
                break
            case ContentTypeModel.User:
                organizationId = item.memberships?.[0]?.organization?.id
                break
            default:
                organizationId = item?.organization?.id
        }
        if (!(item?.id && organizationId)) {
            this.notifications.showError("Missing item or item organization details")
            return
        }
        const dataObject = await this.uploadService.createAndUploadDataObject(files[0], {organizationId: organizationId}, {processUpload: true})
        await this.galleryImageService.assign(item, dataObject.id)
        await this.thumbnails.waitUntilAvailable(dataObject.id, false)
        this.refresh.item(item)
    }

    MimeType = MimeType
    protected readonly ThumbnailLayout = ThumbnailLayout
    protected readonly DownloadResolution = DownloadResolution
}
